import { Box, Slide, Flex } from '@chakra-ui/react';
import { useRef, useLayoutEffect, useState, useEffect } from 'react';
import MessageInput from './MessageInput';
import ResultsContainer from './ResultsContainer';
import Message from './Message';
import { useAuth0 } from '@auth0/auth0-react';

function ChatContainer({
  messages,
  messageInputProps,
  activeConversationId,
  searchResults,
  searchParameters,
  totalSearchResultsCount,
  showLoadingAnimation,
  setShowLoadingAnimation,
  showProviderLoadingScreen,
  setShowProviderLoadingScreen,
  openPositiveFeedbackModal,
  openNegativeFeedbackModal,
}) {
  const { user } = useAuth0();
  const containerRef = useRef();
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const programmaticScroll = useRef(false); // flag to ignore programmatic scrolls
  const [showResultsContainer, setShowResultsContainer] = useState(
    !!searchResults && !!searchParameters,
  );
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isSlideAnimationInProgress, setIsSlideAnimationInProgress] =
    useState(false);

  useEffect(() => {
    setShowResultsContainer(!!searchResults && !!searchParameters);
    setShowProviderLoadingScreen(false);
  }, [searchResults, searchParameters, totalSearchResultsCount]);

  useEffect(() => {
    setLoadingMessages(true);
  }, [activeConversationId]);

  useLayoutEffect(() => {
    if (loadingMessages) {
      // scroll down once messages are loaded for new conversation
      scrollToBottom();
      setLoadingMessages(false);
    } else if (isAtBottom || !userHasScrolled) {
      // if new messages come in we should scroll to the bottom only if the user hasn't scrolled
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    let animationFrameId;
    const performScroll = () => {
      if (isSlideAnimationInProgress) {
        const container = containerRef.current.firstChild;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
        animationFrameId = requestAnimationFrame(performScroll);
      }
    };
    if (isSlideAnimationInProgress) {
      animationFrameId = requestAnimationFrame(performScroll);
    }
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isSlideAnimationInProgress]);

  const scrollToBottom = () => {
    const container = containerRef.current.firstChild;
    if (container) {
      programmaticScroll.current = true; // enable programmatic scroll
      container.scrollTop = container.scrollHeight;
    }
  };

  const handleScroll = () => {
    // Ignore programmatic scrolls
    if (programmaticScroll.current) {
      programmaticScroll.current = false;
      return;
    }

    if (!userHasScrolled) {
      setUserHasScrolled(true);
    }

    const container = containerRef.current.firstChild;
    if (container) {
      const atBottom =
        container.scrollTop + container.clientHeight + 5 >=
        container.scrollHeight;
      if (atBottom) {
        programmaticScroll.current = true;
      }
      setIsAtBottom(atBottom);
    }
  };

  const getMessageComponent = (message, i) => {
    const userName = message.sender === 'user' ? user.name : 'CareJourney';
    const userImage = message.sender === 'user' ? user.picture : null;
    return (
      <Message
        key={i}
        messageData={message}
        userName={userName}
        userImage={userImage}
        openPositiveFeedbackModal={openPositiveFeedbackModal}
        openNegativeFeedbackModal={openNegativeFeedbackModal}
      />
    );
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      justifyContent='space-between'
      flex='1'
      overflowY='scroll'
      padding='15px'
      onScroll={handleScroll}
      background='#EAEBEC'
      borderRadius='8px'
    >
      <Flex
        ref={containerRef}
        direction='column'
        justifyContent='space-between'
        height='100%'
      >
        <Box
          display='flex'
          flexDirection='column'
          overflowY='auto'
          onScroll={handleScroll}
          width={showResultsContainer ? '45%' : '100%'}
          transition='width 0.3s ease-in-out'
          flex='1'
        >
          {messages.map((message, i) => getMessageComponent(message, i))}
          {showLoadingAnimation && (
            <Message
              messageData={{
                text: 'Cara is searching through more than 1,000,000 doctors',
              }}
              animate={true}
            />
          )}
        </Box>
        <Box
          width={showResultsContainer ? '45%' : '100%'}
          pt={5}
          transition='width 0.3s ease-in-out'
        >
          <MessageInput
            {...messageInputProps}
            setShowLoadingAnimation={setShowLoadingAnimation}
            setShowProviderLoadingScreen={setShowProviderLoadingScreen}
          />
        </Box>
      </Flex>
      {showResultsContainer && (
        <Slide
          direction='right'
          in={showResultsContainer}
          onAnimationStart={() => {
            setIsSlideAnimationInProgress(true);
          }}
          onAnimationComplete={() => {
            setIsSlideAnimationInProgress(false);
          }}
          style={{
            position: 'absolute',
            width: '55%',
            right: 0,
          }}
        >
          <ResultsContainer
            providers={searchResults}
            totalSearchResultsCount={totalSearchResultsCount}
            isLoading={showProviderLoadingScreen}
            searchParameters={searchParameters}
          />
        </Slide>
      )}
    </Box>
  );
}

export default ChatContainer;
